import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

import { russianMonths } from "./calendarUtils";
import { DaysGrid } from "./DaysGrid";
import { InfoPanel } from "./InfoPanel";
import { ProfitBadge } from "./ProfitBadge";

// Slide animation direction
type SlideDirection = "left" | "right" | null;

const GRID_WIDTH_REM = 12;
const GRID_GAP_REM = 1;

// Grid width (12rem) + gap (1rem) = 13rem
const SLIDE_OFFSET_REM = 13;

export interface DayStat {
  date: string;
  percent: number;
  profitUsd?: number;
  trades?: number;
}

export interface MonthStat {
  year: number;
  month: number;
  percent: number;
  profitUsd?: number;
  trades?: number;
}

const EMPTY_DAY_STATS: DayStat[] = [];
const EMPTY_MONTH_STATS: MonthStat[] = [];

interface CalendarWidgetProps {
  initialDate?: Date;
  dayStats?: DayStat[];
  monthStats?: MonthStat[];
  className?: string;
  /** Called when month changes (for lazy loading) */
  onMonthChange?: (newMonth: Date) => void;
}

// ============ Calendar Widget ============
export const CalendarWidget = ({
  initialDate = new Date(),
  dayStats = EMPTY_DAY_STATS,
  monthStats = EMPTY_MONTH_STATS,
  className,
  onMonthChange,
}: CalendarWidgetProps) => {
  const [currentDate, setCurrentDate] = useState<Date>(initialDate);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [slideDirection, setSlideDirection] = useState<SlideDirection>(null);
  const isAnimating = useRef(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const dayStatsMap = useMemo(() => {
    const m = new Map<string, number>();
    dayStats.forEach((d) => m.set(d.date, d.percent));
    return m;
  }, [dayStats]);

  const hoveredDay = useMemo(
    () => dayStats.find((d) => d.date === hoveredDate) ?? null,
    [dayStats, hoveredDate]
  );

  const selectedDay = useMemo(
    () => dayStats.find((d) => d.date === selectedDate) ?? null,
    [dayStats, selectedDate]
  );

  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;

  const monthSummary = useMemo(
    () => monthStats.find((m) => m.year === year && m.month === month) ?? null,
    [monthStats, year, month]
  );

  const prevMonthSummary = useMemo(
    () => monthStats.find((m) => m.year === prevYear && m.month === prevMonth) ?? null,
    [monthStats, prevYear, prevMonth]
  );

  const nextMonthSummary = useMemo(
    () => monthStats.find((m) => m.year === nextYear && m.month === nextMonth) ?? null,
    [monthStats, nextYear, nextMonth]
  );

  const handlePrev = useCallback(() => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setSlideDirection("right");
  }, []);

  const handleNext = useCallback(() => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setSlideDirection("left");
  }, []);

  const handleTransitionEnd = useCallback(() => {
    let newDate: Date | null = null;

    if (slideDirection === "left") {
      newDate = new Date(year, month + 1, 1);
    } else if (slideDirection === "right") {
      newDate = new Date(year, month - 1, 1);
    }

    if (newDate) {
      setCurrentDate(newDate);
      onMonthChange?.(newDate);
    }

    setSlideDirection(null);
    isAnimating.current = false;
  }, [slideDirection, year, month, onMonthChange]);

  const handleSelectDay = (date: string) => {
    setSelectedDate((prev) => (prev === date ? null : date));
  };

  // Для инфо-панели используем hovered или selected день
  const activeDay = hoveredDay ?? selectedDay;

  return (
    <div
      className={cn(
        // Layout
        "flex flex-col gap-6 p-4 sm:flex-row sm:items-center",
        // Style
        "rounded-mds bg-neutral-200/2",
        // Width
        "w-full sm:w-134",
        className
      )}
    >
      {/* Left side - Calendar */}
      <div className="flex w-full flex-col items-center gap-3 sm:w-76">
        {/* Month navigation */}
        <div className="order-2 flex w-full items-center justify-between sm:order-1">
          <Button
            variant="neutral"
            buttonType="icon"
            size="sm"
            onClick={handlePrev}
          >
            <ChevronLeft />
          </Button>

          {/* Month name with slide animation - fixed height container */}
          <div className="relative h-8 flex-1 overflow-hidden">
            {/* Previous month - hidden LEFT, appears when sliding right */}
            <div
              className={cn(
                "absolute inset-0 flex items-center justify-center",
                slideDirection && "transition-all duration-300 ease-in-out",
                slideDirection === "right"
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-full opacity-0"
              )}
            >
              <div className="flex items-center gap-0.5">
                <span className="fs-500 font-bold whitespace-nowrap text-white">
                  {russianMonths[prevMonth]}
                </span>
                {prevMonthSummary && (
                  <ProfitBadge
                    value={prevMonthSummary.profitUsd ?? 0}
                    type="currency"
                    size="sm"
                  />
                )}
              </div>
            </div>

            {/* Current month - exits in slide direction */}
            <div
              className={cn(
                "absolute inset-0 flex items-center justify-center",
                slideDirection && "transition-all duration-300 ease-in-out",
                slideDirection === "left" && "-translate-x-full opacity-0",
                slideDirection === "right" && "translate-x-full opacity-0",
                !slideDirection && "translate-x-0 opacity-100"
              )}
            >
              <div className="flex items-center gap-0.5">
                <span className="fs-500 font-bold text-white">{russianMonths[month]}</span>
                {monthSummary && (
                  <ProfitBadge
                    value={monthSummary.profitUsd ?? 0}
                    type="currency"
                    size="sm"
                  />
                )}
              </div>
            </div>

            {/* Next month - hidden RIGHT, appears when sliding left */}
            <div
              className={cn(
                "absolute inset-0 flex items-center justify-center",
                slideDirection && "transition-all duration-300 ease-in-out",
                slideDirection === "left"
                  ? "translate-x-0 opacity-100"
                  : "translate-x-full opacity-0"
              )}
            >
              <div className="flex items-center gap-0.5">
                <span className="fs-500 font-bold whitespace-nowrap text-white">
                  {russianMonths[nextMonth]}
                </span>
                {nextMonthSummary && (
                  <ProfitBadge
                    value={nextMonthSummary.profitUsd ?? 0}
                    type="currency"
                    size="sm"
                  />
                )}
              </div>
            </div>
          </div>

          <Button
            variant="neutral"
            buttonType="icon"
            size="sm"
            onClick={handleNext}
          >
            <ChevronRight />
          </Button>
        </div>

        {/* Days grids container: prev month | current month | next month */}
        {/* Container clips side months, showing only edges */}
        <div className="order-1 relative w-full overflow-hidden sm:order-2">
          {/* Sliding track - animates on month change */}
          <div
            className={cn(
              "relative flex items-start gap-4",
              // Fixed grid height for 6 weeks
              "h-42",
              // Enable transition only when animating
              slideDirection && "transition-transform duration-300 ease-in-out"
            )}
            style={{
              // Slide offset based on direction
              transform: `
                translateX(calc(50% - ${(GRID_WIDTH_REM * 1.5 + GRID_GAP_REM)}rem))
                ${
                  slideDirection === "left"
                    ? ` translateX(-${SLIDE_OFFSET_REM}rem)`
                    : slideDirection === "right"
                      ? ` translateX(${SLIDE_OFFSET_REM}rem)`
                      : ""
                }
              `,
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {/* Previous month - dimmed, becomes active on slide right */}
            <DaysGrid
              year={prevYear}
              month={prevMonth}
              dayStatsMap={dayStatsMap}
              className={cn(
                "pointer-events-none opacity-5",
                slideDirection && "transition-opacity duration-300 ease-in-out",
                slideDirection === "right" && "opacity-100"
              )}
            />

            {/* Current month - becomes dimmed on slide */}
            <DaysGrid
              year={year}
              month={month}
              dayStatsMap={dayStatsMap}
              selectedDate={selectedDate}
              onSelectDay={handleSelectDay}
              onHoverDay={setHoveredDate}
              className={cn(
                slideDirection && "transition-opacity duration-300 ease-in-out",
                slideDirection ? "opacity-5" : "opacity-100"
              )}
            />

            {/* Next month - dimmed, becomes active on slide left */}
            <DaysGrid
              year={nextYear}
              month={nextMonth}
              dayStatsMap={dayStatsMap}
              className={cn(
                "pointer-events-none opacity-5",
                slideDirection && "transition-opacity duration-300 ease-in-out",
                slideDirection === "left" && "opacity-100"
              )}
            />
          </div>
        </div>
      </div>

      {/* Separator mobile (horizontal) */}
      <div
        className="h-px w-[85%] mx-auto sm:hidden"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='2' viewBox='0 0 18 2'%3E%3Cline x1='0' y1='1' x2='9' y2='1' stroke='rgba(249%2C249%2C249%2C0.12)' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat-x",
          backgroundSize: "18px 2px",
        }}
      />

      {/* Separator desktop (vertical) */}
      <div
        className="hidden w-0.5 shrink-0 self-stretch sm:block"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='2' height='100%25' viewBox='0 0 2 18'%3E%3Cline x1='1' y1='0' x2='1' y2='9' stroke='rgba(249%2C249%2C249%2C0.12)' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat-y",
          backgroundSize: "2px 18px",
        }}
      />

      {/* Right side - Info Panel */}
      <div className="w-full min-w-0 sm:flex-1">
        <InfoPanel
          hoveredDay={activeDay}
          monthSummary={monthSummary}
        />
      </div>
    </div>
  );
};
